import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';

import { graphql } from 'babel-plugin-relay/macro';
import { Tab, Tabs, TabsProps, Typography } from 'embeds/components';
import { map } from 'lodash';

import { TabsChatActivities_EmbedQuery$key } from './__generated__/TabsChatActivities_EmbedQuery.graphql';
import BadgeChatActivityUnassigned from './BadgeChatActivityUnassigned';
import BadgeChatActivityUnread from './BadgeChatActivityUnread';

export enum TabsChatActivitiesTab {
  MyChats = 'myChats',
  Unassigned = 'unassigned',
  Assigned = 'assigned',
  Closed = 'closed',
}

export const messagesTabsChatActivities = defineMessages({
  myChats: {
    id: 'deprecated.fragments.TabsChatActivities.myChats',
    defaultMessage: 'My Chats',
  },
  unassigned: {
    id: 'deprecated.fragments.TabsChatActivities.unassigned',
    defaultMessage: 'Unassigned',
  },
  assigned: {
    id: 'deprecated.fragments.TabsChatActivities.assigned',
    defaultMessage: 'Assigned',
  },
  closed: {
    id: 'deprecated.fragments.TabsChatActivities.closed',
    defaultMessage: 'Closed',
  },
});

export interface TabsChatActivitiesProps extends Omit<TabsProps, 'classes'> {
  fragmentRef: TabsChatActivities_EmbedQuery$key;
  includeTabs?: TabsChatActivitiesTab[];
}

const TabsChatActivities: React.FC<TabsChatActivitiesProps> = ({
  fragmentRef,
  includeTabs = [
    TabsChatActivitiesTab.MyChats,
    TabsChatActivitiesTab.Unassigned,
    TabsChatActivitiesTab.Assigned,
    TabsChatActivitiesTab.Closed,
  ],
  ...rest
}) => {
  const { viewer } = useFragment<TabsChatActivities_EmbedQuery$key>(
    graphql`
      fragment TabsChatActivities_EmbedQuery on Query {
        viewer {
          ...BadgeChatActivityUnassigned_EmbedViewer
        }
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();

  const tabs = useMemo(
    () =>
      map(includeTabs, (tab) => {
        if (viewer && tab === TabsChatActivitiesTab.MyChats) {
          return (
            <Tab
              key={tab}
              label={
                <BadgeChatActivityUnread variant="standard-right" color="secondary">
                  <Typography variant="inherit" style={{ marginRight: '15px' }}>
                    {formatMessage(messagesTabsChatActivities[tab])}
                  </Typography>
                </BadgeChatActivityUnread>
              }
              value={tab}
              minWidth
            />
          );
        }
        if (viewer && tab === TabsChatActivitiesTab.Unassigned) {
          return (
            <Tab
              key={tab}
              label={
                <BadgeChatActivityUnassigned fragmentRef={viewer} variant="dot-right" color="secondary">
                  <Typography variant="inherit">{formatMessage(messagesTabsChatActivities[tab])}</Typography>
                </BadgeChatActivityUnassigned>
              }
              value={tab}
              minWidth
            />
          );
        }
        return <Tab key={tab} label={formatMessage(messagesTabsChatActivities[tab])} value={tab} />;
      }),
    [includeTabs, viewer, formatMessage],
  );

  return <Tabs {...rest}>{tabs}</Tabs>;
};

export default TabsChatActivities;
